import { useQuery } from '@tanstack/react-query';
import { campaignKeys } from '@/features/campaign/queries';
import { api } from '@/lib/api';

/**
 * The donation screens' reads of campaign data — docs/decisions/0088. Each is
 * behind the permission of the screen that asks, never a campaign permission,
 * and each key sits under `campaignKeys.all`, so a campaign or category write
 * refreshes these as it refreshes the management screens.
 */
export const donationCampaignKeys = {
  all: [...campaignKeys.all, 'donation'] as const,
  entry: [...campaignKeys.all, 'donation', 'entry'] as const,
  entryForm: (campaignId: string) =>
    [...campaignKeys.all, 'donation', 'entryForm', campaignId] as const,
  entryCurrencies: (periodId: string) =>
    [...campaignKeys.all, 'donation', 'entryCurrencies', periodId] as const,
  filter: (screen: DonationCampaignScreen) =>
    [...campaignKeys.all, 'donation', 'filter', screen] as const,
};

/**
 * The campaigns the entry form offers: what the caller's seat may collect for.
 * The route is `donation.create`'s, so a component mounted where a caller may
 * not create donations passes `enabled: false` rather than earn a refusal.
 */
export function useEntryCampaigns(enabled = true) {
  return useQuery({
    queryKey: donationCampaignKeys.entry,
    queryFn: () => api.donation.campaigns.entry({}),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** The campaign's current published form, which a new donation is entered against. */
export function useEntryForm(campaignId: string, enabled: boolean) {
  return useQuery({
    queryKey: donationCampaignKeys.entryForm(campaignId),
    queryFn: () => api.donation.campaigns.entryForm({ campaignId }),
    enabled,
    staleTime: 0,
  });
}

/** The currencies the season a save lands in prices itself in. */
export function useEntryCurrencies(periodId: string | null) {
  return useQuery({
    queryKey: donationCampaignKeys.entryCurrencies(periodId ?? ''),
    queryFn: () => api.donation.campaigns.entryCurrencies({ campaignPeriodId: periodId as string }),
    enabled: periodId !== null,
  });
}

/**
 * The menu the sidebar draws. The server caches it for a minute per caller, so
 * this is deliberately not refetched on every mount.
 */
export function useDonationCampaignNavigation(enabled: boolean) {
  return useQuery({
    queryKey: campaignKeys.navigation,
    queryFn: () => api.donation.campaigns.navigation({}),
    staleTime: 60_000,
    enabled,
  });
}

export type DonationCampaignScreen = 'list' | 'queue' | 'trash';

const FILTER_READS = {
  list: () => api.donation.campaigns.listFilter({}),
  queue: () => api.donation.campaigns.queueFilter({}),
  trash: () => api.donation.campaigns.trashFilter({}),
} as const;

/** A donation screen's campaign, category and season filters, behind that screen's own permission. */
export function useDonationCampaignFilter(screen: DonationCampaignScreen, enabled = true) {
  return useQuery({
    queryKey: donationCampaignKeys.filter(screen),
    queryFn: FILTER_READS[screen],
    staleTime: 60_000,
    enabled,
  });
}
